import {
  ItemResponsePayload,
  Vignette,
  VignettePresentedVariant,
  VignetteResponse,
} from '../types/Vignette';
import { BlockType } from '../types/Study';
import axiosInstance from './axiosCreator';
import { AxiosError } from 'axios';
import { store } from '../store/store';
import {
  addVignetteResponse,
  vignetteReceived,
} from '../slices/vignettes/vignettesSlice';
import {
  decrementTodayQBankTotal,
  incrementTodayQBankTotal,
} from '../slices/todayStudyHistory/todayStudyHistorySlice';

const vignetteAnswerMutationSeqById = new Map<string, number>();

const getNextVignetteAnswerMutationSeq = (vignetteId: string) => {
  const nextSeq = (vignetteAnswerMutationSeqById.get(vignetteId) ?? 0) + 1;
  vignetteAnswerMutationSeqById.set(vignetteId, nextSeq);
  return nextSeq;
};

const isLatestVignetteAnswerMutationSeq = (
  vignetteId: string,
  mutationSeq: number
) => vignetteAnswerMutationSeqById.get(vignetteId) === mutationSeq;

export class VignetteSchedulingError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'VignetteSchedulingError';
    this.status = status;
    this.code = code;
  }
}

const isFirstAttemptSubmitted = ({
  isSubmitted,
  attemptNumber,
  isRetry,
}: {
  isSubmitted: boolean;
  attemptNumber?: number;
  isRetry?: boolean;
}) => {
  const resolvedAttemptNumber = attemptNumber ?? 1;
  return isSubmitted && !isRetry && resolvedAttemptNumber === 1;
};

const isValidPresentedVariantPayload = (
  payload: unknown
): payload is Vignette =>
  Boolean(
    payload &&
    typeof payload === 'object' &&
    (payload as Vignette).schemaVersion === 'v2' &&
    typeof (payload as Vignette).presentedVariantId === 'string' &&
    (payload as Vignette).presentedVariant &&
    (payload as Vignette).presentedVariant?.id ===
      (payload as Vignette).presentedVariantId
  );

// Answer a vignette
export const answerTutorVignette = async ({
  vignetteId,
  vignetteVariantId,
  responseId,
  userAnswer,
  correctAnswer,
  isSubmitted,
  blockId,
  mode,
  attemptNumber,
  isRetry,
  luckyGuess,
  isLuckyGuessUpdate,
  selectionSequence,
  studyMs,
  source,
  countsTowardTodayHistory = true,
  response: itemResponse,
  optimisticItem,
}: {
  vignetteId: string;
  vignetteVariantId?: string;
  responseId?: string;
  userAnswer: string;
  correctAnswer: string;
  isSubmitted: boolean;
  blockId?: string;
  mode?: BlockType;
  attemptNumber?: number;
  isRetry?: boolean;
  luckyGuess?: boolean;
  isLuckyGuessUpdate?: boolean;
  // Ordered distinct option letters the user selected for this response, in
  // click order. The caller accumulates it and resends the full array on each
  // write; the backend stores it verbatim on the response.
  selectionSequence?: string[];
  // Engaged (tab-visible) ms spent before answering, measured client-side and
  // persisted authoritatively as this question's study time.
  studyMs?: number;
  // Sent ONLY by tutor mode's "Reveal Answer" button (ORA-579). The row that
  // click writes is otherwise identical to a correct "Try Again" retry, so
  // this is the only thing that tells the two apart server-side; an ordinary
  // pick sends nothing, and a backend that predates ORA-578 ignores it.
  source?: 'reveal';
  countsTowardTodayHistory?: boolean;
  // NCLEX item types only (docs/nclex-item-api.md in ora-be): the structured
  // answer. With it, `userAnswer` / `correctAnswer` are sent as "" and the
  // backend grades `response`. Multiple choice never sends it, so its request
  // is unchanged.
  response?: ItemResponsePayload;
  // Item types only: the optimistic row's answer/score fields (canonical
  // answer string, client score), computed by the caller from the variant.
  optimisticItem?: Partial<VignetteResponse>;
}) => {
  const mutationSeq = getNextVignetteAnswerMutationSeq(vignetteId);

  const shouldOptimisticallyIncrement =
    countsTowardTodayHistory &&
    !isLuckyGuessUpdate &&
    isFirstAttemptSubmitted({
      isSubmitted,
      attemptNumber,
      isRetry,
    });

  const normalizedResponseId =
    responseId && responseId.trim().length > 0 ? responseId : undefined;

  if (shouldOptimisticallyIncrement) {
    store.dispatch(incrementTodayQBankTotal());
  }

  try {
    // Optimistic update of vignette response
    const optimisticResponse = {
      id: normalizedResponseId ?? '',
      correct: correctAnswer === userAnswer,
      answer: userAnswer,
      correctAnswer: correctAnswer,
      ...(luckyGuess !== undefined ? { luckyGuess } : {}),
      isSubmitted: isSubmitted,
      submissionDate: isSubmitted ? new Date().toISOString() : null,
      attemptNumber: attemptNumber ?? 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      blockId,
      mode,
      isRetry,
      ...(itemResponse !== undefined
        ? { correct: false, ...optimisticItem }
        : {}),
    };

    store.dispatch(
      addVignetteResponse({ vignetteId, response: optimisticResponse })
    );

    // Update vignette response in BE
    const response = await axiosInstance.put(
      `/api/v5/qbank/vignettes/${vignetteId}/answer`,
      {
        vignetteVariantId,
        responseId: normalizedResponseId,
        userAnswer: userAnswer,
        correctAnswer: correctAnswer,
        isSubmitted: isSubmitted,
        ...(luckyGuess !== undefined ? { luckyGuess } : {}),
        ...(selectionSequence !== undefined ? { selectionSequence } : {}),
        ...(studyMs !== undefined ? { studyMs } : {}),
        ...(source !== undefined ? { source } : {}),
        ...(itemResponse !== undefined ? { response: itemResponse } : {}),
        blockId,
        mode,
        attemptNumber,
        isRetry,
      }
    );

    const responsePayload = response.data.data as unknown;
    if (responsePayload) {
      if (!isLatestVignetteAnswerMutationSeq(vignetteId, mutationSeq)) {
        return responsePayload;
      }
      if (!isValidPresentedVariantPayload(responsePayload)) {
        throw new Error(
          'Invalid V2 vignette payload: missing or inconsistent presented variant'
        );
      }
      store.dispatch(vignetteReceived(responsePayload as Vignette));
      return responsePayload;
    }

    if (shouldOptimisticallyIncrement) {
      store.dispatch(decrementTodayQBankTotal());
    }
    return response.data.data;
  } catch (error) {
    if (shouldOptimisticallyIncrement) {
      store.dispatch(decrementTodayQBankTotal());
    }
    console.error(error);
  }
};

export type ChatVignetteAnswerStatus = 'success' | 'limit_reached' | 'error';

// The variant the backend scheduled in response to a chat answer, when one
// exists. `content` extends VignetteContent so it feeds the shared
// ScheduledQuestionPreviewModal directly.
export type ChatScheduledVariant = {
  content: VignettePresentedVariant;
  dueAt?: string | null;
};

export type ChatVignetteAnswerResult = {
  status: ChatVignetteAnswerStatus;
  scheduledVariant: ChatScheduledVariant | null;
  /**
   * The response the server holds after the write (success only). A repeat
   * answer is a no-op that hands back the response already recorded, so this
   * is the student's real answer, which may not be the one just sent.
   */
  recordedResponse?: VignetteResponse | null;
};

// Answer a practice question served in AI chat. Registers the response with
// source "chat" so it counts toward the free-plan daily question limit and
// feeds scheduling, without touching the qbank block Redux state. On success,
// surfaces any variant the backend scheduled (present on missed questions) so
// the caller can show the "Scheduled a Variant" badge.
export const submitChatVignetteAnswer = async ({
  vignetteId,
  vignetteVariantId,
  userAnswer,
  correctAnswer,
  lectureId,
  studySetId,
  blockId,
}: {
  vignetteId: string;
  vignetteVariantId?: string;
  userAnswer: string;
  correctAnswer: string;
  // The document (lecture) the chat is grounded in, when answering from a
  // document chat — attributes the answer to that lecture's exam server-side.
  lectureId?: string;
  // The study set this question was served in (ORA-677), so the item is marked
  // done against the set. Ignored server-side when the set is not the caller's.
  studySetId?: string;
  // The block the question belongs to, when it is answered outside the block
  // page: a course QBANK step (Courses V1) records its answers in its block.
  blockId?: string;
}): Promise<ChatVignetteAnswerResult> => {
  try {
    const response = await axiosInstance.put<{ data?: Vignette }>(
      `/api/v5/qbank/vignettes/${vignetteId}/answer`,
      {
        ...(vignetteVariantId ? { vignetteVariantId } : {}),
        ...(lectureId ? { lectureId } : {}),
        ...(studySetId ? { studySetId } : {}),
        ...(blockId ? { blockId } : {}),
        userAnswer,
        correctAnswer,
        isSubmitted: true,
        source: 'chat',
      }
    );

    store.dispatch(incrementTodayQBankTotal());

    const vignette = response.data?.data;
    const nextVariantPreview = vignette?.nextVariantPreview ?? null;

    const scheduledVariant: ChatScheduledVariant | null = nextVariantPreview
      ? { content: nextVariantPreview, dueAt: vignette?.scheduling?.dueAt }
      : null;

    return {
      status: 'success',
      scheduledVariant,
      recordedResponse: vignette?.response ?? null,
    };
  } catch (error) {
    if (
      error instanceof AxiosError &&
      error.response?.data?.code === 'DAILY_QBANK_LIMIT'
    ) {
      return { status: 'limit_reached', scheduledVariant: null };
    }
    console.error(error);
    return { status: 'error', scheduledVariant: null };
  }
};

/**
 * Updates vignette scheduling data with optional parameters
 * @param vignetteId - The ID of the vignette to update
 * @param data - Optional scheduling data to update
 * @returns The updated vignette scheduling data or undefined if no data provided
 */
export const updateVignetteScheduling = async (
  vignetteId: string,
  data: {
    state?: number;
    questionHighlights?: string;
    answerCrossouts?: boolean[];
    flagged?: boolean;
    notes?: string;
  },
  options?: {
    blockId?: string;
    vignetteVariantId?: string;
    /**
     * Upsert the response into the vignettes slice (default). The in-chat
     * question block (ORA-820) turns this off: that slice is the open QBank
     * block's question list, and a vignette served in chat is not in it.
     */
    syncStore?: boolean;
  }
) => {
  // Return early if no data is provided
  if (!data || Object.keys(data).length === 0) {
    return;
  }

  // Filter out undefined values to only send present parameters
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );

  // Return early if no valid data after filtering
  if (Object.keys(filteredData).length === 0) {
    return;
  }

  try {
    const response = await axiosInstance.patch(
      `/api/v5/qbank/vignettes/${vignetteId}/scheduling`,
      {
        ...filteredData,
        ...(options?.blockId ? { blockId: options.blockId } : {}),
        ...(options?.vignetteVariantId
          ? { vignetteVariantId: options.vignetteVariantId }
          : {}),
      }
    );

    // Dispatch the updated vignette to Redux store
    if (response.data.data && options?.syncStore !== false) {
      store.dispatch(vignetteReceived(response.data.data));
    }

    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError<{
      error?: string;
      code?: string;
    }>;

    const status = axiosError.response?.status;

    const message =
      axiosError.response?.data?.error ||
      axiosError.message ||
      'Unable to update vignette scheduling';

    const code = axiosError.response?.data?.code;

    throw new VignetteSchedulingError(message, status, code);
  }
};
